import { MENUS } from "../../common/constants";

/*
 Utility helpers to build dynamic routes that include path parameters
*/
const generateBeneficiariesRoutes = (instance) => [
  {
    name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
    pathname: `/account-primary-beneficiaries/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
    pathname: `/account-contingent-beneficiaries/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

const generatePershingBeneficiariesRoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id,
    pathname: `/pershing-primary-beneficiaries/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
    pathname: `/pershing-contingent-beneficiaries/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

const generateInheritedIRARoutes = (instance) => [
  {
    name: MENUS.CLIENT.INHERITEDIRA.id,
    pathname: `/decedent/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  }
];

const generatePershingInheritedIRARoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_DECEDENT.id,
    pathname: `/pershing-decedent/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  }
];

const generateTrustAccountRoutes = (instance) => [
  {
    name: MENUS.CLIENT.TRUST_INFO.id,
    pathname: `/trust-info/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.TRUSTEES.id,
    pathname: `/trustees/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.GRANTORS.id,
    pathname: `/grantors/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

const generatePershingTrustAccountRoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_TRUST_INFO.id,
    pathname: `/pershing-trust-info/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.TRUSTEES.id,
    pathname: `/trustees/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.GRANTORS.id,
    pathname: `/grantors/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

const generateEntityAccountRoutes = (instance) => [
  {
    name: MENUS.CLIENT.ENTITY_INFO.id,
    pathname: `/entity-info/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.AUTHORIZED_SIGNERS.id,
    pathname: `/authorized-signers/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

const generatePershingEntityAccountRoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_ENTITY_INFO.id,
    pathname: `/pershing-entity-info/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
  {
    name: MENUS.CLIENT.AUTHORIZED_SIGNERS.id,
    pathname: `/authorized-signers/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  },
];

// Pershing-specific route generators for new pages
const generatePershingMaritalStatusRoute = () => ({
  name: MENUS.CLIENT.PERSHING_MARITAL_STATUS.id,
  pathname: `/pershing-marital-status`,
});

const generatePershingJointAccountInfoRoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id,
    pathname: `/pershing-joint-account-info/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  }
];

const generatePershingKYCRoutes = (instance) => [
  {
    name: MENUS.CLIENT.PERSHING_KYC.id,
    pathname: `/pershing-kyc/${instance.name}/${instance.label}`,
    params: { name: instance.name, label: instance.label },
  }
];

const generateCustomQuestionRoute = (id) => ({
  name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
  pathname: `/custom-question/${id}`,
  params: { questionId: id },
});


export class GenerateInterviewQuestionsArray {
  interviewPages = [];
  withEmploymentPages = false;
  availablePages = new Set();

  /*
    @param {Object} clientData – contains accounts etc.
    @param {Array} progress    – clientProgress.progress array (list of pages present in template)
    @param {Array} customQuestionIds – list of custom-question _ids that exist
  */
  constructor(clientData, progress, customQuestionIds) {
    this.generateQuestionsArray(clientData, progress, customQuestionIds);
  }

  /*
    Build ordered list of pages, keeping only those that actually exist in the
    template (progress array).
  */
  generateQuestionsArray(clientData, progress, customQuestionIds) {
    this.availablePages = new Set(progress ? progress.map((p) => p.pageName) : []);

    // Helper that checks if a page is available in the template
    const hasPage = (pageId) => this.availablePages.has(pageId);

    /*
      1. Identity / Basic pages (fixed order, include only if present)
    */
    const orderedPages = [];

    [
      MENUS.CLIENT.CITIZENSHIP.id,
      MENUS.CLIENT.LEGAL_NAME.id,
      MENUS.CLIENT.LEGAL_ADDRESS.id,
      MENUS.CLIENT.SSN.id,
      MENUS.CLIENT.DOB.id,
    ].forEach((pid) => {
      if (hasPage(pid)) orderedPages.push(pid);
    });

    /* 1.5. Pershing Marital Status (after DOB, before phone) */
    if (hasPage(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id)) {
      orderedPages.push(generatePershingMaritalStatusRoute());
    }

    /* 1.6. Continue with phone */
    if (hasPage(MENUS.CLIENT.ALTERNATE_PHONE.id)) {
      orderedPages.push(MENUS.CLIENT.ALTERNATE_PHONE.id);
    }

    /* 2. Employment status – some templates have EMPLOYMENT_STATUS, others EMPLOYMENT_STATUS_2 */
    if (hasPage(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id)) {
      orderedPages.push(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);
    } else if (hasPage(MENUS.CLIENT.EMPLOYMENT_STATUS.id)) {
      orderedPages.push(MENUS.CLIENT.EMPLOYMENT_STATUS.id);
    }

    /* 3. The employment-related detail pages (will be injected later via addEmploymentPages) */

    /* 4. Wealth source, Pershing Employer Risk, & PEP – place Employer Risk immediately after Wealth Source when available */
    // 4a. Wealth Source
    if (hasPage(MENUS.CLIENT.WEALTH_SOURCE.id)) {
      orderedPages.push(MENUS.CLIENT.WEALTH_SOURCE.id);
    }
    
    // 4c. PEP
    if (hasPage(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id)) {
      orderedPages.push(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id);
    }

    /* 5. Company official & Beneficial interest */
    [
      MENUS.CLIENT.COMPANY_OFFICIAL.id,
      MENUS.CLIENT.BENEFICIAL_INTEREST.id,
    ].forEach((pid) => {
      if (hasPage(pid)) orderedPages.push(pid);
    });

    /* 6. Account-specific beneficiary pages (only for instances that exist in progress) */
    const beneficiaryPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id)
      : [];

    const pershingBeneficiaryPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id)
      : [];

    if (beneficiaryPages.length) {
      const beneficiaryRoutes = beneficiaryPages
        .map((p) => generateBeneficiariesRoutes({ name: p.instance, label: p.label }))
        .flat();
      orderedPages.push(...beneficiaryRoutes);
    }

    if (pershingBeneficiaryPages.length) {
      const pershingBeneficiaryRoutes = pershingBeneficiaryPages
        .map((p) => generatePershingBeneficiariesRoutes({ name: p.instance, label: p.label }))
        .flat();
      orderedPages.push(...pershingBeneficiaryRoutes);
    }

    /* 6.5. Pershing Joint Account Info pages (for Joint/ManagedJoint accounts only) */
    const pershingJointAccountInfoPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_JOINT_ACCOUNT_INFO.id)
      : [];

    if (pershingJointAccountInfoPages.length) {
      pershingJointAccountInfoPages.forEach((p) => {
        orderedPages.push(...generatePershingJointAccountInfoRoutes({ name: p.instance, label: p.label }));
      });
    }


    /* 7. Inherited IRA (Decedent) pages present in template */
    if (hasPage(MENUS.CLIENT.INHERITEDIRA.id)) {
      const decedentPages = progress
        ? progress.filter((p) => p.pageName === MENUS.CLIENT.INHERITEDIRA.id)
        : [];

      const inheritedIRARoutes = decedentPages
        .map((p) => generateInheritedIRARoutes({ name: p.instance, label: p.label }))
        .flat();
      orderedPages.push(...inheritedIRARoutes);
    }

    /* 7.5. Pershing Inherited IRA (Decedent) pages */
    if (hasPage(MENUS.CLIENT.PERSHING_DECEDENT.id)) {
      const pershingDecedentPages = progress
        ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_DECEDENT.id)
        : [];

      const pershingInheritedIRARoutes = pershingDecedentPages
        .map((p) => generatePershingInheritedIRARoutes({ name: p.instance, label: p.label }))
        .flat();
      orderedPages.push(...pershingInheritedIRARoutes);
    }

    /* 8. Trust Account top-level pages present in template */
    const trustAccountPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.TRUST_ACCOUNT.id)
      : [];

    if (trustAccountPages.length) {
      trustAccountPages.forEach((p) => {
        orderedPages.push({
          name: MENUS.CLIENT.TRUST_ACCOUNT.id,
          pathname: `/trust-account/${p.instance}/${p.label}`,
          params: { name: p.instance, label: p.label },
        });
      });
    }

    /* 9. Entity Account top-level pages present in template */
    const entityAccountPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.ENTITY_ACCOUNT.id)
      : [];

    if (entityAccountPages.length) {
      entityAccountPages.forEach((p) => {
        orderedPages.push({
          name: MENUS.CLIENT.ENTITY_ACCOUNT.id,
          pathname: `/entity-account/${p.instance}/${p.label}`,
          params: { name: p.instance, label: p.label },
        });
      });
    }

    /* 10. Trust Account sub-pages present in template */
    const trustInfoPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.TRUST_INFO.id)
      : [];
    
    if (trustInfoPages.length) {
      trustInfoPages.forEach((p) => {
        orderedPages.push(...generateTrustAccountRoutes({ name: p.instance, label: p.label }));
      });
    }

    /* 10.5. Pershing Trust Account sub-pages */
    const pershingTrustInfoPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_TRUST_INFO.id)
      : [];
    
    if (pershingTrustInfoPages.length) {
      pershingTrustInfoPages.forEach((p) => {
        orderedPages.push(...generatePershingTrustAccountRoutes({ name: p.instance, label: p.label }));
      });
    }

    /* 11. Entity Account pages present in template */
    const entityInfoPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.ENTITY_INFO.id)
      : [];
    
    if (entityInfoPages.length) {
      entityInfoPages.forEach((p) => {
        orderedPages.push(...generateEntityAccountRoutes({ name: p.instance, label: p.label }));
      });
    }

    // 11.4. Pershing KYC pages (for Corporate accounts only) - moved before Entity Info
    const pershingKYCPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_KYC.id)
      : [];

    if (pershingKYCPages.length) {
      pershingKYCPages.forEach((p) => {
        orderedPages.push(...generatePershingKYCRoutes({ name: p.instance, label: p.label }));
      });
    }

    // 11.5. Pershing Entity Account pages present in template
    const pershingEntityInfoPages = progress
      ? progress.filter((p) => p.pageName === MENUS.CLIENT.PERSHING_ENTITY_INFO.id)
      : [];

    if (pershingEntityInfoPages.length) {
      pershingEntityInfoPages.forEach((p) => {
        orderedPages.push(...generatePershingEntityAccountRoutes({ name: p.instance, label: p.label }));
      });
    }

    /* 12. Custom questions (retain server ordering) */
    if (customQuestionIds && customQuestionIds.length) {
      orderedPages.push(
        ...customQuestionIds.map((id) => generateCustomQuestionRoute(id)),
      );
    }

    /* 13. Consolidated statement (if present) */
    if (hasPage(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id)) {
      orderedPages.push(MENUS.CLIENT.CONSOLIDATED_STATEMENT.id);
    }
    // Pershing consolidated statement variant
    if (hasPage(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id)) {
      orderedPages.push(MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id);
    }

    /* 14. Finish */
    orderedPages.push(MENUS.CLIENT.FINISH.id);

    this.interviewPages = orderedPages;
  }

  /*
    Show / hide the additional employment-detail pages based on the user's answer.
  */
  addEmploymentPages() {
    if (this.withEmploymentPages) return; // already added

    // Determine after which index to insert – right after employment status page
    const employmentIdx = this.interviewPages.findIndex(
      (page) =>
        (typeof page === "string" ? page : page.name) ===
        (this.interviewPages.includes(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id)
          ? MENUS.CLIENT.EMPLOYMENT_STATUS_2.id
          : MENUS.CLIENT.EMPLOYMENT_STATUS.id),
    );

    const insertAt = employmentIdx >= 0 ? employmentIdx + 1 : this.interviewPages.length;

    // Decide which detail pages should be considered based on template type
    const usingStatus2 = this.interviewPages.includes(MENUS.CLIENT.EMPLOYMENT_STATUS_2.id);

    const pagesToMaybeAdd = usingStatus2
      ? [
          MENUS.CLIENT.BUSINESS.id,
          MENUS.CLIENT.ANNUAL_INCOME.id,
          MENUS.CLIENT.COMPANY_ADDRESS.id,
          MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id,
        ] // NO job page for status 2 flow
      : [
          MENUS.CLIENT.COMPANY_ADDRESS.id,
          MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id,
          MENUS.CLIENT.JOB_DESCRIPTION.id,
          MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id,
        ];

    const pagesToInsert = pagesToMaybeAdd.filter(
      (pid) =>
        this.availablePages.has(pid) && // must exist in template
        this.interviewPages.every((p) => (typeof p === "string" ? p : p.name) !== pid),
    );

    this.interviewPages.splice(insertAt, 0, ...pagesToInsert);
    this.withEmploymentPages = true;
  }

  removeEmploymentPages() {
    if (!this.withEmploymentPages) return;
    this.interviewPages = this.interviewPages.filter((p) => {
      const id = typeof p === "string" ? p : p.name;
      return (
        id !== MENUS.CLIENT.BUSINESS.id &&
        id !== MENUS.CLIENT.ANNUAL_INCOME.id &&
        id !== MENUS.CLIENT.COMPANY_ADDRESS.id &&
        id !== MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id &&
        id !== MENUS.CLIENT.JOB_DESCRIPTION.id &&
        id !== MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id
      );
    });
    this.withEmploymentPages = false;
  }

  addContingentBeneficiaryPage(name, label) {
    const firstPrimBenIdx = this.interviewPages.findIndex(
      (page) =>
        typeof page === "object" &&
        page.params &&
        page.params.label === label &&
        (page.name === MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id || 
        page.name === MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id),
    );

    if (firstPrimBenIdx === -1) return;

    const page = this.interviewPages[firstPrimBenIdx];
    const isPershing = page.name === MENUS.CLIENT.PERSHING_PRIMARY_BENEFICIARIES.id;

    if (isPershing) {
      this.interviewPages.splice(firstPrimBenIdx + 1, 0, {
        name: MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id,
        pathname: `/pershing-contingent-beneficiaries/${name}/${label}`,
        params: { name, label },
      });
    } else {
      this.interviewPages.splice(firstPrimBenIdx + 1, 0, {
        name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        pathname: `/account-contingent-beneficiaries/${name}/${label}`,
        params: { name, label },
      });
    }

  }

  removeContingentBeneficiaryPage(label) {
    this.interviewPages = this.interviewPages.filter(
      (page) =>
        !(
          typeof page === "object" &&
          page.params &&
          page.params.label === label &&
          (page.name === MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id || 
          page.name === MENUS.CLIENT.PERSHING_CONTINGENT_BENEFICIARIES.id)
        ),
    );
  }

  /* helpers for callers */
  getInterviewPages() {
    return this.interviewPages;
  }

  getWithEmploymentPages() {
    return this.withEmploymentPages;
  }
}

// Utility: Check if any trust or entity account pages exist in progress
export function hasTrustOrEntityPages(progress) {
  if (!progress || !Array.isArray(progress)) return false;
  return progress.some(
    (p) =>
      p.pageName === MENUS.CLIENT.TRUST_ACCOUNT.id ||
      p.pageName === MENUS.CLIENT.ENTITY_ACCOUNT.id ||
      p.pageName === MENUS.CLIENT.TRUST_INFO.id ||
      p.pageName === MENUS.CLIENT.PERSHING_TRUST_INFO.id ||
      p.pageName === MENUS.CLIENT.TRUSTEES.id ||
      p.pageName === MENUS.CLIENT.GRANTORS.id ||
      p.pageName === MENUS.CLIENT.ENTITY_INFO.id ||
      p.pageName === MENUS.CLIENT.AUTHORIZED_SIGNERS.id
  );
}
